/**
 * App.tsx - FMwvTool v4 with Full OpCentral Orchestrator Integration
 * 
 * ARCHITECTURE:
 * - All 6 orchestrators: Actr, Profylr, Aggregatr, Integratr, Synkr, Loggr
 * - Pluggr plugin system for all tools
 * - Modern context patterns
 * - Standards-compliant UI (context menus, multi-select, undo/redo)
 * - Comprehensive samples system
 * 
 * CHANGE HISTORY:
 * - 251108_0920 Complete migration - Big Bang approach
 */

import { useEffect, useState, useMemo, lazy, Suspense } from 'react';
// ✅ INTEGRATION MANAGER PATTERN: Single provider with all dependencies
import { AppProvider } from '@opcentral/providr';
import {
  // Hooks
  useActr,
  useProfylr,
  useAggregatr,
  useInterfacr,
  useNotifyr,
  useSkinnr,
  useOrganizr,
  useBacktrackr,
  useCommandr,
  // Components
  GlobalSamplesPanel,
  NotificationBadge,
  Panel,
  Spreadsheet as TableViewer,
  FavList,
  VisualMapper,
  PipelineMapper,
  // Utilities
  getPluggr,
  getLoggr, ConsoleAdapter,
  getTemplatr,
  Favr,
  // Additional providers (not in AppProvider)
  IntegratrProvider,
  SynkrProvider,
  OrganizrProvider,
  TaggrProvider
} from '@opcentral/dependr';

// Initialize Pluggr plugin registry (triggers self-registration)
// Note: Importing from dependr which re-exports from pluggr
import '@opcentral/pluggr/plugins';

// Local imports
import { FMwvToolProvider } from './context/FMwvToolContext';
import { createFMwvToolContext, getPluginMenuItems, registerMenuItemCallback, unregisterMenuItemCallback } from './pluginContext';
import { detectFileMaker } from './utils/filemakerDetection';
import { getFileMakerBridge } from './filemaker/FileMakerBridge';
import { parseURLParams, decodeData } from './utils/urlParams';
import './styles/main.css';
import './styles/appearance-overrides.css';
import '../../../packages/panelr/src/styles/panels.css';
import type { ToolType } from './types';
import { NavigationWrapper, NavigationSidebar } from './navigation/NavigationWrapper';
import { NavigationSidebarV2 } from './navigation/NavigationSidebarV2';
import { TOOLS } from './tools';
import { getEnabledPlugins, getPluginStats } from './pluginRegistry';
import { PluginsPanel } from './components/PluginsPanelRefactored';
import { preloadConfig } from './config/preload.config';
import { registerServiceWorker } from './utils/serviceWorker';
import { initializePluginComponents, getPluginComponent } from './pluginComponentRegistry';

// Lazy-loaded Components (loaded only when needed)
const PDFViewer = lazy(() => import('./components/PDFViewer').then(m => ({ default: m.PDFViewer })));
const OfficeViewer = lazy(() => import('./components/OfficeViewer').then(m => ({ default: m.OfficeViewer })));
const DataTransformer = lazy(() => import('./components/DataTransformer').then(m => ({ default: m.DataTransformer || m.default })));
const FileMakerBridge = lazy(() => import('./components/FileMakerBridge').then(m => ({ default: m.FileMakerBridge })));
const Rmemb = lazy(() => import('./components/Rmemb').then(m => ({ default: m.Rmemb })));
const QuickReference = lazy(() => import('./components/QuickReference/QuickReference').then(m => ({ default: m.QuickReference })));
const DocumentationPanel = lazy(() => import('./components/DocumentationPanel/DocumentationPanel').then(m => ({ default: m.DocumentationPanel })));
const SetupWizard = lazy(() => import('./components/SetupWizard/SetupWizard').then(m => ({ default: m.SetupWizard })));
const FileLoader = lazy(() => import('./components/FileLoader').then(m => ({ default: m.FileLoader })));
const TagManagerPlugin = lazy(() => import('./components/TagManagerPlugin').then(m => ({ default: m.TagManagerPlugin })));
const PickerSelectr = lazy(() => import('./components/PickerSelectr').then(m => ({ default: m.PickerSelectr })));
const SelectrExamples = lazy(() => import('./components/SelectrExamples'));
const DevPanel = lazy(() => import('./components/DevPanel').then(m => ({ default: m.DevPanel })));
const SettingsPanel = lazy(() => import('./components/SettingsPanelConfiggr').then(m => ({ default: m.SettingsPanelConfiggr })));
const FieldMatcher = lazy(() => import('./components/FieldMatcherRefactored').then(m => ({ default: m.FieldMatcherRefactored })));
const OutlinerPluginView = lazy(() => import('./plugins/outliner/OutlinerPluginView').then(m => ({ default: m.OutlinerPluginView })));
const PackageTest = lazy(() => import('./components/PackageTest').then(m => ({ default: m.PackageTest })));
const CommandPalette = lazy(() => import('./components/CommandPalette').then(m => ({ default: m.CommandPalette })));

// URL Info Modal and Customizer (not lazy-loaded since they're small and used in header)
import { URLInfoModal } from './components/URLInfoModal';
import { ToolCustomizer } from './components/ToolCustomizer';
import { VERSION } from './version';

// Skinnr and Templatr integration
import { registerFMwvToolSkins } from './skins';
import { registerFMwvToolTemplates, getTemplate } from './templates/fmwvtool-templates';
import { useSkinConfig } from './hooks/useSkinConfig';

// Configure Loggr for browser-only (console adapter only, no HTTP)
const loggr = getLoggr({ 
  category: 'fmwvtool',
  adapters: [new ConsoleAdapter({ minLevel: 'debug' })]
});

/**
 * Skinnr & Templatr Initializer - Registers skins and templates
 */
function SkinnrInitializer({ children }: { children: React.ReactNode }) {
  const skinnrContext = useSkinnr() as any; // Type assertion for now
  const actr = useActr(); // Get actr instance from context
  const [initialized, setInitialized] = useState(false);
  
  useEffect(() => {
    if (!skinnrContext || initialized) return;
    
    // Register FMwvTool skins
    registerFMwvToolSkins(skinnrContext);
    console.log('[Skinnr] Registered FMwvTool skins:', skinnrContext.getAllSkins().map((s: any) => s.id));
    
    // Register Templatr templates with actr instance
    const templatr = getTemplatr({ actr });
    registerFMwvToolTemplates(templatr);
    console.log('[Templatr] Registered FMwvTool templates');
    
    // Check URL for skin parameter
    const params = new URLSearchParams(window.location.search);
    const skinParam = params.get('skin');
    const templateParam = params.get('template');
    
    // Apply skin from URL or default
    if (skinParam) {
      const skin = skinnrContext.getSkin(skinParam);
      if (skin) {
        skinnrContext.applySkin(skinParam);
        console.log('[Skinnr] Applied skin from URL:', skinParam);
      } else {
        console.warn('[Skinnr] Skin not found:', skinParam, '- using auto-switching default');
        skinnrContext.applySkin('fmwvtool-default');
      }
    } else {
      // Apply auto-switching skin (light/dark based on system preference)
      skinnrContext.applySkin('fmwvtool-default');
      console.log('[Skinnr] Applied auto-switching skin: fmwvtool-default');
    }
    
    // Apply template from URL if specified
    if (templateParam) {
      const template = getTemplate(templateParam);
      if (template) {
        console.log('[Templatr] Applying template from URL:', templateParam, template.config);
        // Template config will be used by components via useSkinnr hook
        // Store template config in skinnr for easy access
        (skinnrContext as any).activeTemplate = template;
        
        // Apply template config to URL parameters so existing code picks it up
        const config = template.config as any;
        if (config.navigation?.hideNav) {
          const currentParams = new URLSearchParams(window.location.search);
          if (!currentParams.has('hideNav')) {
            currentParams.set('hideNav', 'true');
            // Update URL without reload
            const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
            window.history.replaceState({}, '', newUrl);
            console.log('[Templatr] Applied hideNav from template to URL');
          }
        }
        
        if (config.dataSubmission?.saveMode) {
          const currentParams = new URLSearchParams(window.location.search);
          if (!currentParams.has('saveMode')) {
            currentParams.set('saveMode', config.dataSubmission.saveMode);
            const newUrl = `${window.location.pathname}?${currentParams.toString()}`;
            window.history.replaceState({}, '', newUrl);
            console.log('[Templatr] Applied saveMode from template to URL');
          }
        }
      } else {
        console.warn('[Templatr] Template not found:', templateParam);
      }
    }
    
    setInitialized(true);
  }, [skinnrContext, initialized]);
  
  return <>{children}</>;
}

/**
 * Inner App Component - Has access to all orchestrators
 */
function AppContent() {
  const actr = useActr();
  const profylr = useProfylr();
  const aggregatr = useAggregatr();
  const { interfacr } = useInterfacr();
  const notifyr = useNotifyr();
  const skinnr = useSkinnr() as any; // For theme switcher
  const organizr = useOrganizr(); // For List Manager and other Organizr-based plugins
  const backtrackr = useBacktrackr(); // For global undo/redo
  const commandr = useCommandr(); // For keyboard shortcuts
  
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentTheme, setCurrentTheme] = useState<'light' | 'dark' | 'auto'>('auto'); // Theme switcher state
  
  // Load last tool from session storage
  const [currentTool, setCurrentTool] = useState<ToolType>(() => {
    try {
      const saved = sessionStorage.getItem('fmwvtool-current-tool');
      return (saved as ToolType) || 'home';
    } catch {
      return 'home';
    }
  });

  // All useState hooks MUST come before useEffect hooks
  const [favoritesVersion, setFavoritesVersion] = useState(0);
  const [hideNav, setHideNav] = useState(false);
  const [customizeMode, setCustomizeMode] = useState(() => {
    try {
      return localStorage.getItem('fmwvtool-customize') === 'true';
    } catch {
      return false;
    }
  });
  const [navState, setNavState] = useState<NavState>('full');
  const [urlData, setUrlData] = useState<any>(null);
  const [showQuickRef, setShowQuickRef] = useState(false);
  const [showURLInfo, setShowURLInfo] = useState(false);
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showCommandPalette, setShowCommandPalette] = useState(false);
  const [pluginsLoaded, setPluginsLoaded] = useState(false);
  const [pluggr] = useState(() => getPluggr(createFMwvToolContext));
  const [samplesEnabled, setSamplesEnabled] = useState(true);
  const [fmConfig] = useState(() => detectFileMaker());
  const [pluginMenuItems, setPluginMenuItems] = useState<MenuItem[]>([]);
  const [menuCollapsed, setMenuCollapsed] = useState(false);

  // V2 Navigation is now the default (V1 deprecated)
  const useV2Navigation = true;

  // Save tool selection to session storage
  useEffect(() => {
    try {
      sessionStorage.setItem('fmwvtool-current-tool', currentTool);
    } catch (err) {
      console.warn('Failed to save tool to session storage:', err);
    }
  }, [currentTool]);
  
  // Initialize Favr with Actr
  useEffect(() => {
    Favr.initialize({ actr });
  }, [actr]);
  
  // Load saved theme preference on mount
  useEffect(() => {
    try {
      const savedTheme = localStorage.getItem('fmwvtool-theme-mode') as 'light' | 'dark' | 'auto' | null;
      if (savedTheme && savedTheme !== currentTheme) {
        setCurrentTheme(savedTheme);
        
        // For auto mode, detect system preference
        let skinId: string;
        if (savedTheme === 'auto') {
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          skinId = prefersDark ? 'fmwvtool-default-dark' : 'fmwvtool-default-light';
        } else {
          skinId = `fmwvtool-default-${savedTheme}`;
        }
        
        skinnr.applySkin(skinId);
        loggr.info(`Restored theme preference: ${savedTheme}`, 'theme', { skinId, systemPreference: savedTheme === 'auto' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : 'n/a' });
      }
    } catch (err) {
      console.warn('Failed to load theme preference:', err);
    }
  }, []); // Only run on mount
  
  // Navigation version toggle removed - V2 was incomplete

  // Listen for favorites changes
  useEffect(() => {
    const handleStorageChange = () => {
      setFavoritesVersion(v => v + 1);
      // Also update customize mode from localStorage
      try {
        const customizeValue = localStorage.getItem('fmwvtool-customize') === 'true';
        setCustomizeMode(customizeValue);
      } catch (err) {
        console.warn('Failed to read customize mode from localStorage:', err);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Register global keyboard shortcuts via Commandr
  useEffect(() => {
    if (!commandr) return;
    
    // IMPORTANT: System shortcuts (Cmd-Q, Cmd-Tab) are NEVER registered
    // Cmd-W is smart: only intercepts when there's a closeable window/modal
    
    // Command Palette: Cmd+K
    const unregisterCmdK = commandr.register('cmd+k', () => {
      setShowCommandPalette(prev => !prev);
    }, {
      scope: 'global',
      description: 'Toggle Command Palette',
      preventDefault: true,
      allowInInput: false
    });
    
    // Close Window: Cmd+W (smart - only when closeable window exists)
    const unregisterCmdW = commandr.register('cmd+w', () => {
      // Check if any closeable window/modal is open
      if (showCommandPalette) {
        setShowCommandPalette(false);
      } else if (showQuickRef) {
        setShowQuickRef(false);
      } else if (showURLInfo) {
        setShowURLInfo(false);
      } else if (showCustomizer) {
        setShowCustomizer(false);
      } else if (showSettings) {
        setShowSettings(false);
      }
      // If no closeable window, don't preventDefault - let system handle window close
    }, {
      scope: 'global',
      description: 'Close Window/Modal',
      preventDefault: (showCommandPalette || showQuickRef || showURLInfo || showCustomizer || showSettings),
      allowInInput: false
    });
    
    // Backtrackr Undo: Cmd-Z (only if Backtrackr available)
    let unregisterUndo: (() => void) | null = null;
    if (backtrackr) {
      unregisterUndo = commandr.register('cmd+z', () => {
        if (backtrackr.canUndo) {
          backtrackr.undo();
        }
      }, {
        scope: 'global',
        description: 'Undo',
        preventDefault: true,
        allowInInput: false
      });
    }
    
    // Backtrackr Redo: Cmd-Shift-Z (only if Backtrackr available)
    let unregisterRedo: (() => void) | null = null;
    if (backtrackr) {
      unregisterRedo = commandr.register('cmd+shift+z', () => {
        if (backtrackr.canRedo) {
          backtrackr.redo();
        }
      }, {
        scope: 'global',
        description: 'Redo',
        preventDefault: true,
        allowInInput: false
      });
    }
    
    // Cleanup on unmount
    return () => {
      unregisterCmdK();
      unregisterCmdW();
      if (unregisterUndo) unregisterUndo();
      if (unregisterRedo) unregisterRedo();
    };
  }, [commandr, backtrackr, showCommandPalette, showQuickRef, showURLInfo, showCustomizer, showSettings]);

  // Listen for command palette navigation events
  useEffect(() => {
    const handleNavigate = (e: CustomEvent) => {
      if (e.detail && e.detail.tool) {
        setCurrentTool(e.detail.tool);
      }
    };
    window.addEventListener('command-palette-navigate', handleNavigate as EventListener);
    return () => window.removeEventListener('command-palette-navigate', handleNavigate as EventListener);
  }, []);
  
  // Debug: Log when currentTool changes (disabled - too noisy)
  // useEffect(() => {
  //   console.log('[App] currentTool changed to:', currentTool);
  // }, [currentTool]);

  // Debug: log customize button state (disabled - too noisy)
  // useEffect(() => {
  //   console.log('[App] Customize button state:', { customizeMode, hideNav, shouldShow: customizeMode });
  // }, [customizeMode, hideNav]);

  // Combined menu items: internal tools + plugin items
  const allMenuItems: MenuItem[] = useMemo(() => {
    // Convert internal tools to menu items
    const internalItems: MenuItem[] = TOOLS.map(tool => ({
      id: tool.id,
      type: 'action' as const,
      label: tool.name,
      icon: tool.icon,
      category: tool.category,
      tooltip: tool.description,
      action: () => {
        console.log('[App] Menu action called for tool:', tool.id);
        setCurrentTool(tool.id as ToolType);
      },
      metadata: { source: 'internal' }
    }));
    
    return [...internalItems, ...pluginMenuItems];
  }, [pluginMenuItems]);

  // Register callback for plugin menu items
  useEffect(() => {
    const callback = (item: MenuItem) => {
      setPluginMenuItems(prev => {
        // Check if item already exists
        const exists = prev.some(i => i.id === item.id);
        if (exists) {
          // Update existing
          return prev.map(i => i.id === item.id ? item : i);
        } else {
          // Add new
          return [...prev, item];
        }
      });
    };
    
    registerMenuItemCallback(callback);
    
    // Load existing plugin menu items
    const existing = getPluginMenuItems('side');
    if (existing.length > 0) {
      setPluginMenuItems(existing);
    }
    
    return () => {
      unregisterMenuItemCallback(callback);
    };
  }, []);

  // Initialize application
  useEffect(() => {
    const init = async () => {
      try {
        loggr.info('🚀 FMwvTool v4 initializing...', 'app', {});
        
        // Initialize FileMaker bridge
        const fmBridge = getFileMakerBridge();
        loggr.info('FileMaker bridge initialized', 'app', { 
          isFileMaker: fmBridge.isFileMaker 
        });

        // Parse URL parameters
        const params = parseURLParams();
        
        // Load app settings from localStorage FIRST (so URL can override)
        try {
          const savedSettings = localStorage.getItem('fmwvtool-settings');
          if (savedSettings) {
            const settings = JSON.parse(savedSettings);
            if (settings.samplesEnabled !== undefined && params.samples === undefined) {
              setSamplesEnabled(settings.samplesEnabled);
            }
            if (settings.lastTool && !params.tool) {
              setCurrentTool(settings.lastTool);
            }
            loggr.info('Settings loaded from localStorage', 'app', { settings });
          }
        } catch (err) {
          loggr.debug('No saved settings found, using defaults', 'app', {});
        }
        
        // URL parameters override saved settings
        if (params.tool) {
          setCurrentTool(params.tool);
          loggr.debug('Tool from URL (overrides saved)', 'app', { tool: params.tool });
          
          // Hide nav by default when tool param is present, unless showToolbar=true
          if (params.showToolbar !== 'true') {
            setHideNav(true);
          }
        }
        
        if (params.hideNav) {
          setHideNav(true);
        }

        // Check for customize mode
        if (params.customize === 'true') {
          setCustomizeMode(true);
        }
        
        if (params.data) {
          const decoded = decodeData(params.data);
          setUrlData(decoded);
          loggr.debug('Data from URL', 'app', { dataKeys: Object.keys(decoded) });
        }

        // Check for samples parameter
        if (params.samples !== undefined) {
          setSamplesEnabled(params.samples === 'true' || params.samples === '1');
        }

        // Initialize plugin component registry (BEFORE loading plugins)
        initializePluginComponents();
        
        // Register core actions with Actr
        await registerActions();

        // Load plugins
        await loadPlugins();

        setIsReady(true);
        loggr.info('✅ FMwvTool v4 initialized successfully', 'app', {});

        // Register Service Worker for caching
        registerServiceWorker({
          onSuccess: () => loggr.info('Service Worker registered', 'app', {}),
          onUpdate: () => loggr.info('Service Worker updated', 'app', {}),
          onError: (err) => loggr.warn('Service Worker registration failed', 'app', { error: err.message })
        });

        // Preload configured modules in background
        preloadModules();
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Initialization failed';
        setError(errorMsg);
        loggr.error('Initialization error', 'app', { error: errorMsg });
      }
    };

    init();
  }, [actr, profylr, pluggr]);

  /**
   * Register all actions with Actr
   */
  const registerActions = async () => {
    loggr.debug('Registering actions with Actr', 'app', {});

    // Navigation actions
    await actr.register('fmwvtool:navigate', {
      handler: async ({ tool }: { tool: ToolType }) => {
        setCurrentTool(tool);
        loggr.info('Navigated to tool', 'navigation', { tool });
        return { success: true, tool };
      },
      description: 'Navigate to a tool',
    });

    // Settings actions
    await actr.register('fmwvtool:toggle-samples', {
      handler: async () => {
        const newValue = !samplesEnabled;
        setSamplesEnabled(newValue);
        
        // Save to localStorage
        try {
          const settings = {
            samplesEnabled: newValue,
            lastTool: currentTool
          };
          localStorage.setItem('fmwvtool-settings', JSON.stringify(settings));
          loggr.info('Settings saved', 'settings', { settings });
        } catch (err) {
          loggr.error('Failed to save settings', 'settings', { error: err });
        }
        
        loggr.info('Toggled samples', 'settings', { enabled: newValue });
        return { success: true, enabled: newValue };
      },
      description: 'Toggle samples visibility',
    });

    // Quick reference actions
    await actr.register('fmwvtool:toggle-quick-ref', {
      handler: async () => {
        setShowQuickRef(!showQuickRef);
        return { success: true };
      },
      description: 'Toggle quick reference panel',
    });

    // Menu actions
    await actr.register('menu:clear-search', {
      handler: async ({ appName, menuId }: { appName?: string; menuId?: string }) => {
        loggr.debug('Menu search cleared', 'menu', { appName, menuId });
        return { success: true };
      },
      description: 'Clear menu search',
    });

    await actr.register('menu:state-loaded', {
      handler: async (params: any) => {
        loggr.debug('Menu state loaded', 'menu', params);
        return { success: true };
      },
      description: 'Menu state loaded from cache',
    });

    await actr.register('menu:state-saved', {
      handler: async (params: any) => {
        loggr.debug('Menu state saved', 'menu', params);
        return { success: true };
      },
      description: 'Menu state saved to cache',
    });

    await actr.register('menu:item-click', {
      handler: async (params: any) => {
        loggr.debug('Menu item clicked', 'menu', params);
        return { success: true };
      },
      description: 'Menu item clicked',
    });

    await actr.register('menu:item-toggle', {
      handler: async (params: any) => {
        loggr.debug('Menu item toggled', 'menu', params);
        return { success: true };
      },
      description: 'Menu item toggle',
    });

    await actr.register('menu:select-item', {
      handler: async (params: any) => {
        loggr.debug('Menu item selected', 'menu', params);
        return { success: true };
      },
      description: 'Menu item selected',
    });

    await actr.register('menu:toggle-item-expanded', {
      handler: async (params: any) => {
        loggr.debug('Menu item expansion toggled', 'menu', params);
        return { success: true };
      },
      description: 'Toggle menu item expansion',
    });

    loggr.debug('Actions registered', 'app', { count: 10 });
  };

  /**
   * Preload modules in background for faster navigation
   */
  const preloadModules = () => {
    loggr.debug('Preloading modules', 'performance', { 
      immediate: preloadConfig.immediate 
    });

    // Preload immediate modules after a short delay
    setTimeout(() => {
      preloadConfig.immediate.forEach(tool => {
        switch (tool) {
          case 'file-loader':
            import('./components/FileLoader');
            break;
          case 'tag-manager':
            import('./components/TagManagerPlugin');
            break;
          case 'picker':
            import('./components/PickerSelectr');
            break;
          case 'table-viewer':
            // TableViewer is from @opcentral/viewr, already loaded
            break;
          case 'transform-distill':
            import('./components/DataTransformer');
            break;
          case 'filemaker-bridge':
            import('./components/FileMakerBridge');
            break;
          case 'field-matcher':
            import('./components/FieldMatcher');
            break;
        }
      });
      
      loggr.debug('Modules preloaded', 'performance', {});
    }, 1000); // Wait 1s after init before preloading
  };

  /**
   * Load all plugins
   */
  const loadPlugins = async () => {
    loggr.info('📦 Loading plugins...', 'plugins', {});
    
    const enabledPlugins = getEnabledPlugins();
    loggr.debug('Enabled plugins', 'plugins', { 
      plugins: enabledPlugins.map(p => p.name)
    });

    for (const pluginEntry of enabledPlugins) {
      try {
        // Dynamic import of plugin
        const pluginModule = await import(/* @vite-ignore */  pluginEntry.path);
        const plugin = pluginModule.default;

        // Register with Pluggr
        await pluggr.register(plugin);
        await pluggr.enable(plugin.manifest.id);

        loggr.info('Plugin loaded', 'plugins', { 
          plugin: plugin.manifest.name
        });
      } catch (error) {
        loggr.error('Plugin load failed', 'plugins', { 
          plugin: pluginEntry.name, 
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }

    setPluginsLoaded(true);
    
    const stats = getPluginStats();
    loggr.info('✅ Plugins loaded', 'plugins', { stats });
  };

  // Error screen
  if (error) {
    return (
      <div className="error-screen">
        <div className="error-icon">⚠️</div>
        <h1>Initialization Error</h1>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Reload</button>
      </div>
    );
  }

  // Loading screen
  if (!isReady) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <div className="loading-text">FMwvTool v4.0</div>
        <div className="loading-subtext">OpCentral Edition</div>
        <div className="loading-orchestrators">
          <span className="badge">Actr</span>
          <span className="badge">Profylr</span>
          <span className="badge">Aggregatr</span>
          <span className="badge">Integratr</span>
          <span className="badge">Synkr</span>
          <span className="badge">Loggr</span>
        </div>
      </div>
    );
  }

  // Loading fallback for lazy components
  const LoadingFallback = () => (
    <div className="tool-loading">
      <div className="loading-spinner"></div>
      <div className="loading-text">Loading {currentTool}...</div>
    </div>
  );

  // Render tool content
  const renderTool = () => {
    // console.log('[App] renderTool called with currentTool:', currentTool); // Disabled - too noisy
    
    // Home page is hardcoded, not a plugin
    if (currentTool === 'home') {
      // Skip to home page rendering below
    } else {
      // Try to get the tool from plugin component registry
      const PluginComponent = getPluginComponent(currentTool);
      
      if (PluginComponent) {
        // console.log('[App] Loading tool from plugin registry:', currentTool); // Disabled - too noisy
        return (
          <Suspense fallback={<LoadingFallback />}>
            <PluginComponent organizr={(organizr as any).manager} />
          </Suspense>
        );
      }
      
      // Plugin not found - show helpful error
      console.warn('[App] Tool not found in plugin registry:', currentTool);
      const pluginData = pluggr.get(currentTool);
      const pluginEntry = getEnabledPlugins().find(p => p.id === currentTool);
      
      return (
        <div className="plugin-not-found">
          <div className="error-icon">🔌</div>
          <h2>Plugin Not Found</h2>
          <p>The tool <strong>{currentTool}</strong> could not be loaded.</p>
          
          <div className="help-section">
            <h3>To fix this:</h3>
            <ol>
              <li>Add the plugin to <code>src/pluginRegistry.ts</code></li>
              <li>Create the plugin file at the specified path</li>
              <li>Ensure the plugin exports a component</li>
              <li>Reload the application</li>
            </ol>
          </div>
          
          <div className="debug-info">
            <h4>Debug Information:</h4>
            <pre>{JSON.stringify({
              requestedTool: currentTool,
              pluginFound: !!pluginData,
              registryEntry: !!pluginEntry,
              enabledPlugins: getEnabledPlugins().map(p => p.id)
            }, null, 2)}</pre>
          </div>
          
          <div className="error-actions">
            <button onClick={() => window.location.reload()} className="btn-primary">
              Reload Application
            </button>
            <button onClick={() => setCurrentTool('home')} className="btn-secondary">
              Go to Home
            </button>
          </div>
        </div>
      );
    }
    
    // Home page (only hardcoded page remaining - everything else should be a plugin)
    const toolContent = (() => {
      switch (currentTool) {
      case 'home':
      default:
        return (
          <div className="welcome-card">
            <div className="welcome-header">
              <h2>Welcome to FMwvTool v4.0</h2>
              <p>OpCentral-powered FileMaker WebViewer utility</p>
              <div className="orchestrator-badges">
                <span className="badge active">✅ Actr</span>
                <span className="badge active">✅ Profylr</span>
                <span className="badge active">✅ Aggregatr</span>
                <span className="badge active">✅ Integratr</span>
                <span className="badge active">✅ Synkr</span>
                <span className="badge active">✅ Loggr</span>
                <span className="badge active">✅ Pluggr</span>
              </div>
            </div>
            
            {/* Favorites Section */}
            <div className="favorites-section">
              <div className="feature-grid" key={favoritesVersion}>
                <FavList
                  context="fmwvtool-plugins"
                  actr={actr}
                  onItemClick={(fav: any) => {
                    console.log('[Favorites] Navigating to favorite:', fav);
                    const toolId = fav.id || fav.metadata?.id;
                    if (toolId) {
                      setCurrentTool(toolId as ToolType);
                    }
                  }}
                  renderItem={(fav: any) => {
                    const toolId = fav.id || fav.metadata?.id;
                    return (
                    <div 
                      className="feature-card" 
                      onClick={() => {
                        console.log('[Favorites] Card clicked:', toolId);
                        if (toolId) {
                          setCurrentTool(toolId as ToolType);
                        }
                      }}
                    >
                      <button 
                        className="favorite-star-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log('[Favorites] Removing favorite:', fav.id);
                          Favr.remove('fmwvtool-plugins', fav.id);
                          setFavoritesVersion(v => v + 1);
                        }}
                        title="Remove from favorites"
                      >
                        ★
                      </button>
                      <div className="feature-icon">{fav.metadata?.icon || '📦'}</div>
                      <h3>{fav.metadata?.name || fav.id}</h3>
                      <p>{fav.metadata?.description || 'No description'}</p>
                    </div>
                    );
                  }}
                  emptyMessage="No favorites yet. Click the star icon (☆) next to any tool in the sidebar menu to add it here."
                />
              </div>
            </div>

            <div className="welcome-footer">
              <p>
                <strong>Plugins Loaded:</strong> {pluginsLoaded ? getPluginStats().total : 0}
              </p>
              <p>
                <strong>Samples:</strong> {samplesEnabled ? 'Enabled' : 'Disabled'}
                <button onClick={() => actr.execute('fmwvtool:toggle-samples', {})}>
                  Toggle
                </button>
              </p>
            </div>
          </div>
        );
      }
    })();

    // Wrap lazy-loaded components in Suspense
    return (
      <Suspense fallback={<LoadingFallback />}>
        {toolContent}
      </Suspense>
    );
  };

  // Theme switcher function
  const cycleTheme = () => {
    const themes: ('light' | 'dark' | 'auto')[] = ['light', 'dark', 'auto'];
    const currentIndex = themes.indexOf(currentTheme);
    const nextTheme = themes[(currentIndex + 1) % themes.length];
    setCurrentTheme(nextTheme);
    
    // Apply the theme - for auto mode, detect system preference
    let skinId: string;
    if (nextTheme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      skinId = prefersDark ? 'fmwvtool-default-dark' : 'fmwvtool-default-light';
    } else {
      skinId = `fmwvtool-default-${nextTheme}`;
    }
    skinnr.applySkin(skinId);
    
    // Persist theme preference
    try {
      localStorage.setItem('fmwvtool-theme-mode', nextTheme);
    } catch (err) {
      console.warn('Failed to save theme preference:', err);
    }
    
    loggr.info(`Theme switched to ${nextTheme}`, 'theme', { skinId, systemPreference: nextTheme === 'auto' ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light') : 'n/a' });
  };
  
  const themeIcon = currentTheme === 'light' ? '☀️' : currentTheme === 'dark' ? '🌙' : '🌓';

  // Action buttons for navigation bar
  const actionButtons = (
    <>
      <button 
        className="nav-icon-button"
        onClick={cycleTheme}
        title={`Theme: ${currentTheme} (click to cycle)`}
      >
        {themeIcon}
      </button>
      <button 
        className="nav-icon-button"
        onClick={() => setShowURLInfo(true)}
        title="View API documentation for current tool"
      >
        📚
      </button>
      <button 
        className="nav-icon-button"
        onClick={() => setShowCustomizer(true)}
        title="Customize current tool and generate URL"
      >
        🎨
      </button>
      <button 
        className="nav-icon-button"
        onClick={() => setShowSettings(true)}
        title="Application settings"
      >
        ⚙️
      </button>
      {fmConfig.isFileMaker && (
        <div className="filemaker-indicator">
          🔗 FM {fmConfig.environment.version}
        </div>
      )}
    </>
  );

  // Customize mode button (shown when customize mode is enabled, regardless of nav state)
  const customizeButton = customizeMode && (
    <button 
      className="floating-customize-button"
      onClick={() => setShowCustomizer(prev => !prev)}
      title={showCustomizer ? "Close customizer" : "Customize current tool"}
      style={{ 
        position: 'fixed',
        bottom: '20px',
        right: '20px',
        zIndex: 99999,
        width: '60px',
        height: '60px',
        borderRadius: '50%',
        background: '#3b82f6',
        color: 'white',
        border: 'none',
        fontSize: '24px',
        cursor: 'pointer',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      }}
    >
      🎨
    </button>
  );
  

  return (
    <div className="fmwvtool-app">
      {!hideNav && (
        <NavigationWrapper
          currentTool={currentTool}
          onToolChange={setCurrentTool}
          actionButtons={actionButtons}
          useV2Navigation={useV2Navigation}
        />
      )}
      
      {/* Floating customize button in customize mode */}
      {customizeButton}
      
      <div className="app-layout">
        {!hideNav && (
          <Panel
            id="fmwvtool-sidebar"
            position="left"
            defaultWidth={280}
            minWidth={200}
            maxWidth={600}
            widths={{ minimized: 60, normal: 280 }}
            collapsible={true}
            resizable={true}
            persistent={true}
          >
            {useV2Navigation ? (
              <NavigationSidebarV2
                currentTool={currentTool}
                onToolChange={setCurrentTool}
              />
            ) : (
              <NavigationSidebar
                currentTool={currentTool}
                onToolChange={setCurrentTool}
              />
            )}
          </Panel>
        )}
        <main className="tool-content">
          {renderTool()}
        </main>
      </div>

      {showQuickRef && (
        <div className="quick-ref-overlay" onClick={() => setShowQuickRef(false)}>
          <QuickReference />
        </div>
      )}

      {samplesEnabled && currentTool !== 'home' && (
        <GlobalSamplesPanel
          currentTool={currentTool}
          onClose={() => setSamplesEnabled(false)}
          onLoadSample={(sample) => {
            loggr.info('Loading sample', 'samples', { sample, tool: currentTool });
            // Emit event for the current tool to handle
            actr.execute(`${currentTool}:load-sample`, { sample });
            // Show notification
            notifyr.notify({
              message: `Loaded sample: ${sample.name}`,
              type: 'success'
            });
          }}
        />
      )}

      {showURLInfo && (
        <URLInfoModal
          currentTool={currentTool}
          onClose={() => setShowURLInfo(false)}
        />
      )}

      {showCustomizer && (
        <ToolCustomizer
          currentTool={currentTool}
          onClose={() => setShowCustomizer(false)}
          currentSamplesEnabled={samplesEnabled}
          currentNavVisible={!hideNav}
          currentSidebarVisible={!hideNav}
          onSamplesChange={(enabled) => setSamplesEnabled(enabled)}
          onNavVisibilityChange={(showNav, showSidebar) => {
            setHideNav(!showNav);
            // Note: sidebar visibility would need additional state management
          }}
        />
      )}

      {showSettings && (
        <Suspense fallback={<LoadingFallback />}>
          <SettingsPanel onClose={() => setShowSettings(false)} />
        </Suspense>
      )}

      {showCommandPalette && (
        <Suspense fallback={null}>
          <CommandPalette 
            isOpen={showCommandPalette}
            onClose={() => setShowCommandPalette(false)}
          />
        </Suspense>
      )}

      {/* Notification Badge */}
      <NotificationBadge />
    </div>
  );
}

/**
 * Main App Component with all providers
 */
export default function App() {
  // Parse URL parameters for skin selection
  const params = new URLSearchParams(window.location.search);
  const skinParam = params.get('skin') || params.get('theme');
  const skinMode = params.get('skinMode') || params.get('mode');
  
  // Determine initial skin from URL or localStorage
  const getInitialSkin = () => {
    if (skinParam) return skinParam;
    try {
      const saved = localStorage.getItem('fmwvtool-skin');
      return saved || 'compact'; // Default to compact
    } catch {
      return 'compact';
    }
  };
  
  const getInitialMode = () => {
    if (skinMode) return skinMode as 'light' | 'dark' | 'auto';
    try {
      const saved = localStorage.getItem('fmwvtool-skin-mode');
      return (saved as 'light' | 'dark' | 'auto') || 'auto';
    } catch {
      return 'auto';
    }
  };

  return (
    <AppProvider
      skinnrNamespace="fmwvtool"
      skinnrTheme="light"
      enableDragDrop={true}
    >
      <OrganizrProvider>
        <TaggrProvider>
          <IntegratrProvider>
            <SynkrProvider>
              <SkinnrInitializer>
                <FMwvToolProvider>
                  <AppContent />
                </FMwvToolProvider>
              </SkinnrInitializer>
            </SynkrProvider>
          </IntegratrProvider>
        </TaggrProvider>
      </OrganizrProvider>
    </AppProvider>
  );
}
